import { formatDateTimeUrlDate } from 'common/UrlDate';
import { intersectionBy, isEmpty, omit, uniqBy, xor } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { EditableOrderFragment, useEditableOrderLazyQuery } from 'src/gqlReactTypings.generated.d';
import { usePromotionalsHook } from 'src/promotionals/usePromotionalsHook';
import Notifications from 'src/shared/Notifications';
import { useDispatch, useSelector } from 'src/store';
import { resetEditableOrder, setActiveCart, setEditableOrder, setLineItemQuantity } from 'src/store/cart/actions';
import { selectAddOnItems, selectFullLineItems, selectSingleShefCartSubtotal } from 'src/store/cart/selectors';
import { CartActionLocation, CartAddOnItem, CartFoodItem, WholeCartLineItem } from 'src/store/cart/types';

interface IUseOrderCart {
  orderId?: number;
  forceReload?: boolean;
}

export const useOrderCart = ({ orderId, forceReload }: IUseOrderCart) => {
  const [fetchOrder, { data, loading, error }] = useEditableOrderLazyQuery();

  const dispatch = useDispatch();

  const {
    activeCartShefId,
    recentlyAdded,
    lineItems,
    addOnItems,
    subtotal,
    stateOrderId,
    orderCartShefTip,
    rawOrderData,
  } = useSelector(({ editableOrderCart: cart }) => ({
    activeCartShefId: cart.activeCartShefId,
    recentlyAdded: cart.recentlyAdded,
    lineItems: selectFullLineItems(cart),
    addOnItems: selectAddOnItems(cart),
    subtotal: selectSingleShefCartSubtotal(cart, cart.activeCartShefId),
    stateOrderId: cart.orderId,
    orderCartShefTip: cart.shefTip,
    rawOrderData: isEmpty(cart.rawOrderData) ? null : (cart.rawOrderData as EditableOrderFragment),
  }));

  const setQuantity = (
    foodItem: CartFoodItem,
    quantity: number,
    deliveryDate: string,
    location: CartActionLocation
  ) => {
    dispatch(setLineItemQuantity(true, { foodItem, quantity, deliveryDate, location }));
  };

  useEffect(() => {
    if (orderId !== undefined && (forceReload || stateOrderId !== orderId)) {
      fetchOrder({ variables: { orderId } });
      dispatch(setEditableOrder({ orderId }));
    }
  }, [dispatch, forceReload, stateOrderId, orderId]);

  useEffect(() => {
    if (data?.order) {
      const { order } = data;

      const availableAddOnItems = uniqBy(
        order.addOnItems.map(({ addOnItem }) => addOnItem).concat(order.availableAddOnItems),
        'id'
      );

      const now = new Date().toISOString();

      dispatch(
        setEditableOrder({
          ready: true,
          activeCartShefId: order.publicShef.id,
          foodItems: order.lineItems.map(({ foodItem }) => foodItem),
          lineItems: order.lineItems.map((lineItem) => ({
            ...omit(lineItem, 'foodItem'),
            id: lineItem.foodItemId,
            shefId: order.publicShef.id,
            createdAt: now,
            updatedAt: now,
            customizationIds: lineItem.customizationIds,
          })),
          addOnItems: order.addOnItems.map((item) => ({
            ...item.addOnItem,
            shefId: order.shefId,
            quantity: item.quantity,
          })),
          availableAddOnItems,
          promoApplied: order?.promoCode?.value,
          promoCode: order.promoCode?.code,
          recentlyAdded,
          shefTip: order.shefTip ?? 0,
          shefTipPercent: null,
          rawOrderData: data.order,
          shefs: [
            {
              ...order.publicShef,
              imageUrl: order.publicShef.imageUrl || '',
              loading: false,
              syncedAt: new Date().toISOString(),
              name: order.publicShef.firstName || '',
              deliveryDate: order.deliveryDate,
            },
          ],
        })
      );
    }
  }, [data, dispatch]); // eslint-disable-line

  const resetOrderState = useCallback(() => {
    dispatch(resetEditableOrder());
    Notifications.info('Dish editing has been cancelled. No changes to your order were saved.');
  }, [dispatch]);

  const order = rawOrderData;

  const [deliveryDate, setDeliveryDate] = useState<DateTime>();
  useEffect(() => {
    if (order) {
      const orderDeliveryDate = DateTime.fromJSDate(new Date(order.deliveryDate), {
        zone: order.publicShef.shefProfile.timeZone,
      });
      setDeliveryDate(orderDeliveryDate);
    }
  }, [order]);

  const { setCode, unsetActiveCode, code } = usePromotionalsHook();
  useEffect(() => {
    // If the user is editing an order, select the promo code that that order used
    if (order && code !== order?.promoCode && orderId !== undefined) {
      unsetActiveCode();
      setCode(order?.promoCode ?? null);
    }
  }, [unsetActiveCode, order, setCode, code, orderId]);

  const possibleDeliveryDates = useMemo(() => {
    if (order && order.possibleDeliveryDates) {
      return order.possibleDeliveryDates.map((possibleDate: EditableOrderFragment['possibleDeliveryDates'][0]) => ({
        ...possibleDate,
        dateTime: DateTime.fromISO(possibleDate.date, {
          zone: order.publicShef.shefProfile.timeZone,
        }),
      }));
    }
    return [];
  }, [order]);

  const [itemsForDeletion, setItemsForDeletion] = useState<WholeCartLineItem[]>([]);
  const [newDate, setNewDate] = useState<DateTime>();
  const deliveryDateClick = useCallback(
    (newDate: DateTime) => {
      const date = possibleDeliveryDates.find((possibleDeliveryDate: (typeof possibleDeliveryDates)[0]) =>
        possibleDeliveryDate.dateTime.equals(newDate)
      );
      if (date) {
        const unavailableItems = date.unavailableItems.map(({ foodItem }: (typeof date.unavailableItems)[0]) => ({
          foodItemId: foodItem.id,
        }));
        const items = intersectionBy(lineItems, unavailableItems, 'foodItemId');
        if (items.length) {
          setItemsForDeletion(items);
          setNewDate(date.dateTime);
          return;
        }
      }
      setDeliveryDate(newDate);
      dispatch(setActiveCart(true, { shefId: activeCartShefId, deliveryDate: formatDateTimeUrlDate(newDate) }));
    },
    [lineItems, possibleDeliveryDates]
  );

  const clearInvalidItems = useCallback(() => {
    setNewDate(undefined);
    setItemsForDeletion([]);
  }, []);

  const deleteInvalidItems = useCallback(() => {
    if (newDate) {
      itemsForDeletion.forEach((item) => {
        setQuantity(item.foodItem, 0, formatDateTimeUrlDate(newDate), 'cart');
      });
      setDeliveryDate(newDate);
    }
    clearInvalidItems();
  }, [clearInvalidItems, itemsForDeletion, newDate, setQuantity, deliveryDate]);

  const showDeliveryDateModal = useMemo(() => itemsForDeletion.length > 0, [itemsForDeletion.length]);

  const modalProps = useMemo(
    () => ({ itemsForDeletion, onProceed: deleteInvalidItems, onCancel: clearInvalidItems }),
    [clearInvalidItems, deleteInvalidItems, itemsForDeletion]
  );

  const newLineItems = useMemo(
    () =>
      lineItems.map(({ foodItemId, quantity }) => ({
        foodItemId,
        quantity,
      })),
    [lineItems]
  );
  const oldLineItems = useMemo(
    () =>
      (order?.lineItems ?? []).map(({ foodItemId, quantity }: { foodItemId: number; quantity: number }) => ({
        foodItemId,
        quantity,
      })),
    [order]
  );
  const lineItemsDifferent = useMemo(() => {
    const diff = xor(newLineItems, oldLineItems);
    return diff.length > 0;
  }, [newLineItems, oldLineItems]);

  const newAddOnItems = useMemo(() => addOnItems.map(({ id, quantity }) => ({ id, quantity })), [addOnItems]);
  const oldAddOnItems = useMemo(() => (order?.addOnItems ?? []).map(({ id, quantity }) => ({ id, quantity })), [order]);

  const addOnItemsDifferent = useMemo(() => {
    const diff = xor(newAddOnItems, oldAddOnItems);
    return diff.length > 0;
  }, [newAddOnItems, oldAddOnItems]);

  const oldAddOnItemsTotal = useMemo(
    () =>
      (order?.addOnItems ?? []).reduce(
        (acc, addOnItem: { addOnItem: { price: number }; quantity: number }) =>
          acc + addOnItem.quantity * addOnItem.addOnItem.price,
        0
      ),
    [order]
  );
  const newAddOnItemsTotal = useMemo(
    () => addOnItems.reduce<number>((acc, addOnItem: CartAddOnItem) => acc + addOnItem.quantity * addOnItem.price, 0),
    [addOnItems]
  );

  const sendModifyLineItems = useMemo(() => {
    if (order) {
      return (
        order.shefTip !== orderCartShefTip ||
        order.promoCode?.id !== code?.id ||
        lineItemsDifferent ||
        addOnItemsDifferent ||
        order.subtotal !== subtotal ||
        oldAddOnItemsTotal !== newAddOnItemsTotal
      );
    }
    return false;
  }, [
    addOnItemsDifferent,
    code,
    lineItemsDifferent,
    newAddOnItemsTotal,
    oldAddOnItemsTotal,
    order,
    orderCartShefTip,
    subtotal,
  ]);

  return {
    orderId: stateOrderId,
    order: rawOrderData,
    addOnItemsTotal: newAddOnItemsTotal,
    loading,
    error,
    resetOrderState,
    deliveryDateClick,
    deliveryDate,
    showDeliveryDateModal,
    itemsForDeletion,
    deleteInvalidItems,
    clearInvalidItems,
    modalProps,
    sendModifyLineItems,
  };
};
